/* Uploads to Clara, a few at a time, for the whole browser session.

   The queue lives in this module rather than in the dialog, so closing the dialog or leaving
   the page does not lose it. Clara indexes behind a small bounded queue and answers 503 while
   it is full; an upload then waits and tries again, longer each time. */

export const IN_FLIGHT = 2
export const MAX_RETRIES = 20
export const LARGE_UPLOAD = 50

export const UPLOAD_STATE = {
	CHECKING: "checking",
	WAITING: "waiting",
	UPLOADING: "uploading",
	RETRYING: "retrying",
	ATTACHING: "attaching",
	INDEXING: "indexing",
	READY: "ready",
	SKIPPED: "skipped",
	ATTACHED: "attached",
	FAILED: "failed",
}

const ACTIVE = new Set([UPLOAD_STATE.CHECKING, UPLOAD_STATE.WAITING, UPLOAD_STATE.UPLOADING, UPLOAD_STATE.RETRYING, UPLOAD_STATE.ATTACHING, UPLOAD_STATE.INDEXING])

export function isActive(item) {
	return ACTIVE.has(item.state)
}

export function backoffMs(attempt, base = 2000, cap = 30000) {
	return Math.min(cap, base * 2 ** attempt)
}

export async function sha256Hex(file) {
	const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer())
	return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("")
}

// What to do with each file, from Clara's answer to "which of these hashes exist".
export function planUploads(files, hashes, existing = {}) {
	return files.map((file, index) => {
		const found = hashes[index] ? existing[hashes[index]] : null
		if (!found) return { file, action: "upload" }
		return { file, action: found.has_original ? "skip" : "attach", document: found }
	})
}

const defaultSleep = ms => new Promise(resolve => setTimeout(resolve, ms))

export function createUploadQueue({ concurrency = IN_FLIGHT, maxRetries = MAX_RETRIES, sleep = defaultSleep } = {}) {
	let items = []
	let running = 0
	let nextKey = 1
	const listeners = new Set()

	function emit() {
		listeners.forEach(listener => listener())
	}

	function update(key, patch) {
		items = items.map(item => (item.key === key ? { ...item, ...patch } : item))
		emit()
	}

	/* `send(file)` resolves to { status, data } and is given per file, so a later
	   workspace never reaches files queued earlier. */
	function add(files, { send, workspace }) {
		const added = files.map(file => ({
			key: nextKey++, file, name: file.name, size: file.size, workspace, send,
			state: UPLOAD_STATE.CHECKING, reason: null, documentId: null,
		}))
		items = [...items, ...added]
		emit()
		return added.map(item => item.key)
	}

	function start(key) {
		update(key, { state: UPLOAD_STATE.WAITING })
		pump()
	}

	function pump() {
		while (running < concurrency) {
			const next = items.find(item => item.state === UPLOAD_STATE.WAITING)
			if (!next) return
			running += 1
			update(next.key, { state: UPLOAD_STATE.UPLOADING })
			run(next).finally(() => {
				running -= 1
				pump()
			})
		}
	}

	async function run(item) {
		for (let attempt = 0; ; attempt++) {
			let result
			try {
				result = await item.send(item.file)
			} catch (e) {
				update(item.key, { state: UPLOAD_STATE.FAILED, reason: e.message || "Upload failed" })
				return
			}
			const { status, data = {} } = result
			// A full ingest queue clears; "indexing is unavailable" (no OpenAI key) does not.
			if (status === 503 && attempt < maxRetries && !/unavailable/i.test(data.error || "")) {
				const wait = backoffMs(attempt)
				update(item.key, { state: UPLOAD_STATE.RETRYING, reason: `Clara is busy indexing, trying again in ${Math.round(wait / 1000)} s` })
				await sleep(wait)
				update(item.key, { state: UPLOAD_STATE.UPLOADING, reason: null })
				continue
			}
			if (status === 202) {
				update(item.key, { state: UPLOAD_STATE.INDEXING, documentId: data.document?.id ?? null, reason: null })
			} else if (status === 200 && data.duplicate) {
				update(item.key, { state: UPLOAD_STATE.SKIPPED, documentId: data.document?.id ?? null, reason: "Already uploaded" })
			} else {
				update(item.key, { state: UPLOAD_STATE.FAILED, reason: data.error || `Upload failed (${status})` })
			}
			return
		}
	}

	function indexing(workspace) {
		return items.filter(item => item.state === UPLOAD_STATE.INDEXING && item.workspace === workspace && item.documentId != null)
	}

	// Indexing finishes on the server; the page polls the ids from indexing() and hands back what it found.
	function indexed(polledIds, documents) {
		const byId = new Map(documents.map(document => [document.id, document]))
		items.forEach(item => {
			if (item.state !== UPLOAD_STATE.INDEXING || !polledIds.includes(item.documentId)) return
			const document = byId.get(item.documentId)
			if (!document) update(item.key, { state: UPLOAD_STATE.FAILED, reason: "The document was deleted while it was indexing" })
			else if (document.status === "ready") update(item.key, { state: UPLOAD_STATE.READY })
			else if (document.status === "failed") update(item.key, { state: UPLOAD_STATE.FAILED, reason: document.error || "Indexing failed" })
		})
	}

	function clearFinished() {
		items = items.filter(isActive)
		emit()
	}

	return {
		add,
		start,
		update,
		indexing,
		indexed,
		clearFinished,
		items: () => items,
		subscribe: listener => {
			listeners.add(listener)
			return () => listeners.delete(listener)
		},
	}
}

export const uploadQueue = createUploadQueue()
